import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, Twitter, Youtube, Instagram, Facebook, Linkedin, ShoppingBag, MessageCircle, GraduationCap, BookMarked } from "lucide-react";
import { getSetting, saveSetting } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_COLUMNS = [
  { title: "Explore", links: [] },
  { title: "Participate", links: [] },
  { title: "Publisher", links: [] },
];

const SOCIAL_FIELDS = [
  { key: "twitter", label: "X / Twitter", Icon: Twitter, placeholder: "https://x.com/…" },
  { key: "youtube", label: "YouTube", Icon: Youtube, placeholder: "https://youtube.com/@…" },
  { key: "instagram", label: "Instagram", Icon: Instagram, placeholder: "https://instagram.com/…" },
  { key: "facebook", label: "Facebook", Icon: Facebook, placeholder: "https://facebook.com/…" },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin, placeholder: "https://linkedin.com/in/…" },
  { key: "amazon", label: "Amazon Author", Icon: ShoppingBag, placeholder: "https://amazon.in/author/…" },
  { key: "whatsapp", label: "WhatsApp", Icon: MessageCircle, placeholder: "https://wa.me/…" },
  { key: "academia", label: "Academia.edu", Icon: GraduationCap, placeholder: "https://independent.academia.edu/…" },
  { key: "goodreads", label: "Goodreads", Icon: BookMarked, placeholder: "https://goodreads.com/author/show/…" },
];

const EMPTY = {
  publisher_name: "",
  tagline: "",
  mark_line: "",
  mission: "",
  disclaimer: "",
  founder_name: "",
  founder_role: "",
  contact_email: "",
  contact_phone: "",
  address_lines: [""],
  footer_tagline: "",
  footer_columns: DEFAULT_COLUMNS,
  social_links: Object.fromEntries(SOCIAL_FIELDS.map(s => [s.key, ""])),
  nav: [],
  seo_default_description: "",
  seo_default_social_image: "",
};

export default function AdminSiteSettings() {
  const { user } = useAuth();
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const canEdit = user && (user.role === "owner" || user.role === "editor");

  useEffect(() => {
    let cancelled = false;
    getSetting("public").then(d => {
      if (cancelled) return;
      const merged = { ...EMPTY, ...(d || {}) };
      merged.address_lines = merged.address_lines?.length ? merged.address_lines : [""];
      merged.footer_columns = merged.footer_columns?.length ? merged.footer_columns : DEFAULT_COLUMNS;
      merged.social_links = { ...EMPTY.social_links, ...(merged.social_links || {}) };
      merged.nav = merged.nav || [];
      setData(merged);
    }).catch(() => { /* stay on defaults */ });
    return () => { cancelled = true; };
  }, []);

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const setSocial = (k, v) => setData(d => ({ ...d, social_links: { ...d.social_links, [k]: v } }));

  const setAddressLine = (i, v) => setData(d => ({ ...d, address_lines: d.address_lines.map((l, j) => j === i ? v : l) }));
  const addAddressLine = () => setData(d => ({ ...d, address_lines: [...d.address_lines, ""] }));
  const removeAddressLine = (i) => setData(d => ({ ...d, address_lines: d.address_lines.filter((_, j) => j !== i) }));

  const setColTitle = (i, v) => setData(d => ({ ...d, footer_columns: d.footer_columns.map((c, j) => j === i ? { ...c, title: v } : c) }));
  const setColLink = (ci, li, patch) => setData(d => ({
    ...d,
    footer_columns: d.footer_columns.map((c, j) => j === ci ? { ...c, links: c.links.map((l, k) => k === li ? { ...l, ...patch } : l) } : c),
  }));
  const addColLink = (ci) => setData(d => ({ ...d, footer_columns: d.footer_columns.map((c, j) => j === ci ? { ...c, links: [...(c.links || []), { label: "", to: "" }] } : c) }));
  const removeColLink = (ci, li) => setData(d => ({ ...d, footer_columns: d.footer_columns.map((c, j) => j === ci ? { ...c, links: c.links.filter((_, k) => k !== li) } : c) }));

  const setNavItem = (i, patch) => setData(d => ({ ...d, nav: d.nav.map((n, j) => j === i ? { ...n, ...patch } : n) }));
  const addNavItem = () => setData(d => ({ ...d, nav: [...(d.nav || []), { label: "", to: "" }] }));
  const removeNavItem = (i) => setData(d => ({ ...d, nav: d.nav.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = { ...data, address_lines: (data.address_lines || []).filter(l => l.trim()) };
      await saveSetting("public", cleaned);
      toast.success("Site settings saved. Reload the public site to see updates.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-site-settings">
      <PageHeader eyebrow="Settings" title="Site settings" note="Publisher identity, header/footer, contact and social links used across the public site." />
      {!canEdit && (
        <div className="mt-6 p-4 border-l-2 text-sm" style={{ borderColor: "var(--gi-gold)", background: "var(--gi-ivory-2)", color: "var(--gi-charcoal-2)" }}>
          You are signed in as a Reviewer. Editing is disabled.
        </div>
      )}

      <fieldset disabled={!canEdit} className="mt-8 space-y-8">
        <Card title="Publisher identity">
          <Row>
            <Field label="Publisher name" value={data.publisher_name} onChange={v => set("publisher_name", v)} testId="settings-publisher-name" />
            <Field label="Header mark line" value={data.mark_line} onChange={v => set("mark_line", v)} testId="settings-mark-line" />
          </Row>
          <Field label="Tagline" value={data.tagline} onChange={v => set("tagline", v)} testId="settings-tagline" />
          <TextArea label="Mission statement (used in footer)" rows={3} value={data.mission} onChange={v => set("mission", v)} testId="settings-mission" />
          <TextArea label="Editorial disclaimer" rows={3} value={data.disclaimer} onChange={v => set("disclaimer", v)} testId="settings-disclaimer" />
        </Card>

        <Card title="Founder / Author">
          <Row>
            <Field label="Founder name" value={data.founder_name} onChange={v => set("founder_name", v)} testId="settings-founder-name" />
            <Field label="Role / description" value={data.founder_role} onChange={v => set("founder_role", v)} testId="settings-founder-role" />
          </Row>
        </Card>

        <Card title="Contact details">
          <Row>
            <Field label="Contact email" value={data.contact_email} onChange={v => set("contact_email", v)} testId="settings-contact-email" />
            <Field label="Contact phone" value={data.contact_phone} onChange={v => set("contact_phone", v)} testId="settings-contact-phone" />
          </Row>
          <div>
            <label className="gi-label">Postal address (one line per row)</label>
            <div className="space-y-2">
              {(data.address_lines || []).map((line, i) => (
                <div key={`addr-${i}`} className="flex gap-2">
                  <input className="gi-input flex-1" value={line} onChange={e => setAddressLine(i, e.target.value)} data-testid={`settings-address-${i}`} />
                  <button type="button" className="btn-ghost" onClick={() => removeAddressLine(i)}><Trash2 size={12} /></button>
                </div>
              ))}
              <button type="button" className="btn-ghost" onClick={addAddressLine} data-testid="settings-address-add"><Plus size={12} /> Add line</button>
            </div>
          </div>
        </Card>

        <Card title="Social links (leave blank to hide)">
          <div className="grid gap-4 sm:grid-cols-2">
            {SOCIAL_FIELDS.map(({ key, label, Icon, placeholder }) => (
              <div key={key}>
                <label className="gi-label flex items-center gap-2"><Icon size={14} /> {label}</label>
                <input className="gi-input" placeholder={placeholder} value={data.social_links[key] || ""} onChange={e => setSocial(key, e.target.value)} data-testid={`settings-social-${key}`} />
              </div>
            ))}
          </div>
        </Card>

        <Card title="Footer columns">
          <div className="space-y-6">
            {data.footer_columns.map((col, ci) => (
              <div key={`col-${ci}`} className="border p-4" style={{ borderColor: "var(--gi-border)" }}>
                <Field label="Column title" value={col.title} onChange={v => setColTitle(ci, v)} testId={`settings-footer-col-title-${ci}`} />
                <div className="mt-3 space-y-2">
                  {(col.links || []).map((l, li) => (
                    <div key={`col-${ci}-l-${li}`} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                      <input className="gi-input" placeholder="Label" value={l.label} onChange={e => setColLink(ci, li, { label: e.target.value })} />
                      <input className="gi-input" placeholder="/path or https://…" value={l.to} onChange={e => setColLink(ci, li, { to: e.target.value })} />
                      <button type="button" className="btn-ghost" onClick={() => removeColLink(ci, li)}><Trash2 size={12} /></button>
                    </div>
                  ))}
                  <button type="button" className="btn-ghost" onClick={() => addColLink(ci)}><Plus size={12} /> Add link</button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Navigation labels" note="Header main-nav order & labels. Empty list = use defaults.">
          <div className="space-y-2">
            {(data.nav || []).map((n, i) => (
              <div key={`nav-${i}`} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                <input className="gi-input" placeholder="Label" value={n.label} onChange={e => setNavItem(i, { label: e.target.value })} data-testid={`settings-nav-label-${i}`} />
                <input className="gi-input" placeholder="/path" value={n.to} onChange={e => setNavItem(i, { to: e.target.value })} />
                <button type="button" className="btn-ghost" onClick={() => removeNavItem(i)}><Trash2 size={12} /></button>
              </div>
            ))}
            <button type="button" className="btn-ghost" onClick={addNavItem} data-testid="settings-nav-add"><Plus size={12} /> Add nav item</button>
          </div>
        </Card>

        <Card title="Footer tagline & default SEO">
          <Field label="Footer tagline" value={data.footer_tagline} onChange={v => set("footer_tagline", v)} testId="settings-footer-tagline" />
          <TextArea label="Default meta description (used when a page doesn't set its own)" rows={2} value={data.seo_default_description} onChange={v => set("seo_default_description", v)} testId="settings-seo-desc" />
          <Field label="Default social-sharing image URL" value={data.seo_default_social_image} onChange={v => set("seo_default_social_image", v)} testId="settings-seo-img" />
        </Card>
      </fieldset>

      {canEdit && (
        <button onClick={save} disabled={saving} className="btn-maroon mt-8" data-testid="settings-save-btn">
          <Save size={12} /> {saving ? "Saving…" : "Save site settings"}
        </button>
      )}
    </div>
  );
}

const Card = ({ title, note, children }) => (
  <section className="border bg-white" style={{ borderColor: "var(--gi-border)" }}>
    <div className="px-6 py-4 border-b" style={{ borderColor: "var(--gi-border)" }}>
      <div className="font-heading text-xl" style={{ color: "var(--gi-maroon)" }}>{title}</div>
      {note && <p className="mt-1 text-xs" style={{ color: "var(--gi-muted)" }}>{note}</p>}
    </div>
    <div className="p-6 space-y-4">{children}</div>
  </section>
);
const Row = ({ children }) => <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
const Field = ({ label, value, onChange, testId }) => (
  <div>
    <label className="gi-label">{label}</label>
    <input className="gi-input" value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testId} />
  </div>
);
const TextArea = ({ label, value, onChange, rows = 3, testId }) => (
  <div>
    <label className="gi-label">{label}</label>
    <textarea rows={rows} className="gi-input" value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testId} />
  </div>
);
